import { useCallback, useLayoutEffect, useState, type RefObject } from "react";
import { nowScrollTarget } from "./now-scroll.ts";

/**
 * Автопрокрутка календаря к отметке «сейчас»: склейка решения с DOM.
 *
 * Календарь рисует сутки колонками по `--cal-hour` на час, и прокручивается при
 * этом ТЕЛО КАЛЕНДАРЯ (`.cal__body`), а не страница: экран расписания занимает
 * ровно высоту окна и страницей не прокручивается вовсе. До 17.09.2026 экран
 * открывался на первом часе диапазона, то есть вечернее занятие приходилось
 * искать прокруткой вниз, а на телефоне в режиме «День» видимого куска суток
 * хватает часа на четыре.
 *
 * ⚠️ **Величины окна сюда не годятся ни одна.** Утром 17.09.2026 хук двигал
 * окно (`window.scrollTo` по `window.scrollY` и `innerHeight`), и это перестало
 * работать в тот же день, когда календарь получил свою высоту: окно больше не
 * прокручивается, то есть вызов просто не делал бы ничего. Чистая функция
 * `nowScrollTarget` при этом не изменилась ни строкой, ей всё равно, чья
 * коробка приехала: сменилось только то, чем её кормят.
 *
 * ⚠️ **Попытка ровно одна, и это не экономия, а правило.** Просьба гасится в
 * начале эффекта, до всякой работы. Вторая попытка позже (скажем, на тике часов
 * раз в полминуты или на перечитывании недели после действия) вернула бы
 * человека наверх после того, как он сам прокрутил экран, то есть отобрала бы у
 * него управление ровно тем же образом, каким это делает прыжок на занятие из
 * ссылки (`use-addressed-entry.ts`, правило 4).
 *
 * ⚠️ **Попытка ждёт `ready`, то есть ответ по ПОКАЗАННОЙ неделе.** При переходе
 * на другую неделю колонки перерисовываются сразу, а занятия в них ещё прошлые,
 * и диапазон часов календаря (`calendarHourRange` считает его по занятиям
 * недели) после ответа сервера сдвигается. Прокрутка, снятая по старому
 * диапазону, промахнулась бы на разницу этих диапазонов, а второй попытки нет.
 *
 * ⚠️ **`useLayoutEffect`, а не `useEffect`.** Иначе браузер успевает нарисовать
 * кадр с календарём от самого верха, и человек видит прыжок вместо открытого на
 * нужном месте экрана.
 *
 * Разбор в `docs/schedule-calendar-design.md`, раздел 9.
 */
export function useNowScroll(args: {
  /**
   * Сетка в DOM (`.week__body`): в ней ищутся обе нужные коробки, полоса
   * «сейчас» и прокручиваемое тело календаря. Ссылка одна, потому что тело
   * живёт внутри сетки, а заводить второй `ref` через два компонента ради
   * соседнего узла значило бы протащить его сквозь `CalendarGrid`.
   */
  grid: RefObject<HTMLElement | null>;
  /** Данные, покрывающие ПОКАЗАННУЮ неделю, уже приехали и отрисованы. */
  ready: boolean;
  /**
   * Просить ли прокрутку при первом же удобном случае.
   *
   * ⚠️ **Читается РОВНО ОДИН раз, при монтировании.** Дальше просьбой
   * распоряжаются только `ask` и `cancel`, а переданное сюда позже значение хук
   * молча проигнорирует: начальное состояние `useState` и есть это чтение.
   * Передумать после монтирования можно единственным способом, вызовом.
   */
  armed: boolean;
}): { ask: () => void; cancel: () => void } {
  const { grid, ready, armed } = args;
  const [want, setWant] = useState(armed);

  useLayoutEffect(() => {
    if (!want || !ready) return;
    // Просьба снимается ДО работы, а не после удачной прокрутки: попытка
    // потрачена в любом исходе, в том числе когда полосы на экране не нашлось
    setWant(false);

    /*
      Полосы нет, когда показана чужая неделя: `nowMin` у календаря там `null`
      намеренно, текущему времени в чужой неделе стоять негде. Это штатный
      случай, а не сбой: человек, листающий октябрь, прокрутки и не ждёт.

      Тела нет, когда данных ещё нет вовсе: на месте календаря стоит заглушка
      загрузки. Тогда и полосы нет, но проверяем оба: молчаливый `null` у тела
      означал бы прокрутку в никуда.
    */
    const body = grid.current?.querySelector<HTMLElement>(".cal__body") ?? null;
    const line = grid.current?.querySelector<HTMLElement>(".cal__now") ?? null;
    if (body === null || line === null) return;

    /*
      ⚠️ Все четыре величины сняты с ОДНОЙ коробки, с прокручиваемого тела.
      Смешать их с оконными нельзя: положение полосы тогда мерилось бы от
      верхней кромки экрана, а прокрутка от верха календаря, и промах вышел бы
      ровно на высоту всего, что стоит над календарём (шапка экрана, строка
      навигации, полосы каркаса).

      `nowTop` это разность двух `getBoundingClientRect().top`, то есть верх
      полосы В КООРДИНАТАХ ТЕЛА при нынешней прокрутке, ровно тот же смысл, что
      был у оконной координаты. Рамки у тела нет, поэтому граница и поле
      совпадают и вычитать больше нечего.
    */
    const target = nowScrollTarget({
      scrollY: body.scrollTop,
      nowTop: line.getBoundingClientRect().top - body.getBoundingClientRect().top,
      viewport: body.clientHeight,
      maxScroll: body.scrollHeight - body.clientHeight,
    });
    if (target === null) return;

    /*
      ⚠️ Прокручиваем ТЕЛО КАЛЕНДАРЯ и только по вертикали. `scrollIntoView` тут
      нельзя, и с приездом собственной высоты этот довод стал только вернее: он
      двигает всех предков, то есть уехал бы и вбок (у тела теперь
      `overflow: auto` по обеим осям), унеся за левую кромку шкалу часов. Тот же
      довод записан у ленты реплик в
      `apps/web/src/features/slides/SlideTranscript.tsx`.

      ⚠️ Прокрутка МГНОВЕННАЯ, без `behavior: "smooth"`. Календарь, доехавший до
      нужного места к первому же кадру, читается как «так и открылось»;
      анимированный проезд при открытии читается как поломка, и его пришлось бы
      ещё и гасить под `prefers-reduced-motion`. Присваивание `scrollTop`
      мгновенно само по себе, отдельного `behavior` ему не нужно.
    */
    body.scrollTop = target;
  }, [ready, want, grid]);

  return {
    ask: useCallback(() => {
      setWant(true);
    }, []),
    cancel: useCallback(() => {
      setWant(false);
    }, []),
  };
}
